import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, organization, timeoff } from '@cwos/api-client';
import { ErrorPanel, Pill, Refused, ScopeNote, SuccessEmpty } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { authorityGate } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { ListState } from '../ListStates.js';
import { listPhase, nameResolver } from '../listState.js';
import { scopeNoteFor } from '../readScope.js';
import { SettingsField } from '../settings/common.js';
import {
  calendarNote,
  decisionBody,
  decisionButtonLabel,
  describeStatus,
  describeWindow,
  invalidateAfterTimeOffDecision,
  mayDecideTimeOff,
  mayReadTimeOff,
  PENDING_TIMEOFF_KEY,
  TIMEOFF_DECIDE,
  TIMEOFF_READ,
  reviewCalendar,
  timeOffDecisionFailure,
  timeOffPopulation,
  type ReviewCalendar,
  type RunningDecision,
  type TimeOffDecisionFailure,
} from './timeoffReview.js';

/**
 * TIME OFF WAITING ON A DECISION — the reviewer half of `timeoff`.
 *
 * `POST /timeoff/requests/:id/decision` shipped with Task 13 and had no caller
 * in any admin surface, so a request a worker filed could be read by nobody who
 * could act on it and stayed `pending` for good. Lane L88 built the worker half
 * and bound the client functions; this is the other end of the same lifecycle.
 *
 * WHERE THIS LIVES. Beside `TimeCorrections` on `/time/approvals`, for the
 * reason that screen's own header gives: a supervisor should not visit four
 * modules to find what is waiting on them, and the queue that already decides
 * timesheets, mileage and corrections is where a leave decision belongs. It is
 * deliberately NOT on `/settings/timeoff`, which is where the TYPE CATALOGUE is
 * configured — a tenant configuration act, not a daily one.
 *
 * `reason` IS FREE TEXT A WORKER WROTE ABOUT THEIR OWN LIFE. "Chemotherapy",
 * "custody hearing". The server scopes this read to the caller and the workers
 * on their supervisor edge precisely because of it. It is rendered here because
 * a reviewer deciding leave without knowing what it is for is being asked to
 * decide blind — and it goes nowhere else: not into a query string, not into a
 * failure message, not into anything logged.
 */
export function TimeOffApprovals() {
  const permissions = usePermissions();
  const { has } = permissions;
  /**
   * THE READER'S TWO DOORS, EACH WITH FOUR ANSWERS AND NOT TWO — the same
   * change, and the same reason, as `TimeCorrections`. Both sentences below
   * ("not shown to you", "you can read these and not decide them") were chosen
   * by a default-deny boolean that is also `false` when the permission set
   * could not be re-read. This screen is mounted inside `/time/approvals`,
   * which is gated on `timesheet:approve` — a DIFFERENT key — so a reader with
   * that key cached walks through `RequirePermission` under `isError` and gets
   * both sentences from a set nobody could refresh.
   */
  const readGate = authorityGate(permissions, TIMEOFF_READ);
  const decideGate = authorityGate(permissions, TIMEOFF_DECIDE);
  const canRead = mayReadTimeOff(has);
  const canDecide = mayDecideTimeOff(has);
  const canListWorkers = mayListWorkers(permissions);

  const requests = useQuery({
    queryKey: PENDING_TIMEOFF_KEY,
    queryFn: () => timeoff.requests({ status: 'pending' }),
    enabled: canRead,
  });

  // The catalogue, so a row says "Bereavement" rather than a uuid. Same key as
  // the register, so a reader who has the queue has the names.
  const types = useQuery({
    queryKey: ['timeoff', 'types'],
    queryFn: () => timeoff.types(),
    enabled: canRead,
  });

  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canListWorkers,
  });

  /**
   * THE ORGANIZATION'S CALENDAR, when this account is allowed to know it.
   *
   * `GET /organization` is `org:manage`-gated and the request payload carries
   * no zone of its own, so a supervisor holding only `timeoff:review` cannot
   * learn which calendar the days were asked for on. That is a real gap in the
   * API, and the screen states it rather than quietly rendering the reader's
   * own calendar as if it were the organization's. See `reviewCalendar`.
   */
  const org = useQuery({
    queryKey: ['organization', 'detail'],
    queryFn: () => organization.detail(),
    enabled: has('org:manage'),
  });
  const calendar = reviewCalendar({
    timezone: org.data?.organization.timezone,
    permitted: has('org:manage'),
    isLoading: org.isLoading,
    isError: org.isError,
  });
  const zoneNote = calendarNote(calendar);

  // Secondary reads, both of them. A failed directory fetch must not relabel
  // every worker on the queue as somebody the reader may not see, and a failed
  // catalogue read must not relabel every kind of leave.
  const nameOf = useMemo(() => nameResolver(workers, canListWorkers), [workers, canListWorkers]);
  const typeOf = useMemo(() => typeResolver(types), [types]);

  const scopeNote = scopeNoteFor(timeOffPopulation(permissions));
  const rows = requests.data ?? [];

  return (
    <section className="rn-card">
      <h2 className="rn-card-title">Time off waiting on a decision</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        Approving leave books the days: anything that asks whether this worker is away — the
        schedule, the missing-hours flags — answers differently from the moment it is recorded.
        Denying one leaves the days as they stand and tells the worker why, if you write a note.
      </p>

      {/* WHOSE QUEUE THIS IS. The register narrows in the SQL rather than
          refusing, so a reviewer without `timeoff:read_all` is looking at their
          own team's requests under a heading that reads as the organization's,
          and "Nothing waiting" would be a claim about everybody. Above the
          list, so it frames the empty state too. */}
      {canRead && scopeNote ? <ScopeNote scope={scopeNote} /> : null}

      {/* WHOSE CALENDAR THE DATES ARE ON. Null when they are the
          organization's, which is the only case that needs no sentence. */}
      {canRead && zoneNote ? (
        <p className="rn-muted" role="note" style={{ marginTop: 'var(--space-8)' }}>
          {zoneNote}
        </p>
      ) : null}

      {canRead ? (
        <ListState
          phase={listPhase(requests, rows.length)}
          rows={3}
          onRetry={() => void requests.refetch()}
          failedMessage="We couldn’t load time-off requests just now. This is not the same as there being none — somebody may still be waiting on an answer."
          refused={
            <Refused
              heading="Time-off requests are not shown to you"
              message="Your account cannot read the time-off register, so nothing is listed here. It is withheld, not empty."
            />
          }
          empty={
            <SuccessEmpty
              heading="Nothing waiting"
              message="Requests appear here while they are waiting on a decision. Ones already approved, denied or withdrawn are not — this is the queue, not the history."
            />
          }
        >
          <ul className="tc-rows">
            {rows.map((request) => (
              <TimeOffRow
                key={request.id}
                request={request}
                workerName={nameOf(request.workerId)}
                typeName={typeOf(request.typeId)}
                calendar={calendar}
                canDecide={canDecide}
              />
            ))}
          </ul>
        </ListState>
      ) : (
        /* Withheld, unreadable, or still being read. The refusal keeps the
           words it had, because the withheld/empty distinction it draws is
           still exactly right for a reader who genuinely does not hold the
           key. */
        <GateNotice
          gate={readGate}
          what="read the time-off register"
          onRetry={() => permissions.refetch()}
          refusedNode={
            <Refused
              heading="Time-off requests are not shown to you"
              message="Reading the time-off register needs the time-off read permission. This is not an empty queue — it is one you have not been given."
            />
          }
        />
      )}

      {/* The reader can see the queue and cannot act on it. Said out loud,
          because a queue with no buttons is indistinguishable from a queue
          whose buttons failed to render. */}
      {canRead ? (
        <GateNotice
          gate={decideGate}
          what="decide these requests"
          onRetry={() => permissions.refetch()}
          refusedNode={
            <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
              You can read these and not decide them — recording an approval or a denial needs the
              time-off review permission.
            </p>
          }
        />
      ) : null}
    </section>
  );
}

/**
 * Why the KIND of leave is missing, one row at a time — the same three-way
 * distinction `nameResolver` makes about a worker's name, for the same reason.
 *
 * "Time off" is the honest generic when the catalogue answered and this id was
 * not in it: a type can be deactivated, and a request filed against it stays on
 * file. It is NOT the right word for a catalogue read that failed or has not
 * returned, because those are statements about this screen.
 */
function typeResolver(source: {
  data: readonly timeoff.TimeOffType[] | undefined;
  isLoading: boolean;
  isError: boolean;
}): (typeId: string) => string {
  const map = new Map<string, string>();
  for (const t of source.data ?? []) map.set(t.id, t.name);
  return (typeId: string) => {
    const known = map.get(typeId);
    if (known !== undefined) return known;
    if (source.isError) return 'Kind of leave unavailable';
    if (source.data === undefined) return 'Loading…';
    return 'Time off';
  };
}

function TimeOffRow({
  request,
  workerName,
  typeName,
  calendar,
  canDecide,
}: {
  request: timeoff.TimeOffRequest;
  workerName: string;
  typeName: string;
  calendar: ReviewCalendar;
  canDecide: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState<TimeOffDecisionFailure | null>(null);
  /**
   * WHICH DECISION FAILED, so a retry repeats THAT one.
   *
   * `onRetry={() => decide.mutate('approve')}` is the shape that ships the
   * worst available bug here: a reviewer whose DENIAL hit a 500 presses the
   * button under "we couldn't record that decision" and approves the leave
   * instead. Only the `failed` branch gets a retry at all, and it repeats what
   * was attempted.
   */
  const [attempted, setAttempted] = useState<'approve' | 'deny'>('approve');

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'deny') =>
      timeoff.decideRequest(request.id, decisionBody(decision, note)),
    onMutate: (decision: 'approve' | 'deny') => {
      setAttempted(decision);
      setFailure(null);
    },
    onSuccess: () => {
      setOpen(false);
      setNote('');
      invalidateAfterTimeOffDecision(qc);
    },
    onError: (err: unknown) => setFailure(timeOffDecisionFailure(err)),
  });

  const status = describeStatus(request.status);
  /**
   * The decision actually in flight, keyed by ROW as well as by direction.
   *
   * `decide.isPending` is per-row here because each row owns its own mutation,
   * but the direction is not: both buttons read the same flag, and labelling
   * them from it puts "Recording the approval…" on Approve while the denial is
   * in flight. `decide.variables` is the argument the running mutation was
   * dispatched with. The request id is carried too so the helper is the same
   * one a single shared mutation would need.
   */
  const running: RunningDecision | undefined =
    decide.isPending && decide.variables !== undefined
      ? { requestId: request.id, decision: decide.variables }
      : undefined;

  return (
    <li className="tc-row" style={{ display: 'block' }}>
      <div className="tc-row-btn" style={{ cursor: 'default' }}>
        <span className="tc-row-main">
          <span className="tc-row-name">{workerName}</span>
          <span className="rn-muted">
            {typeName} · {describeWindow(request, calendar)}
          </span>
          {/* The worker's own words. Rendered because deciding without them is
              deciding blind; it goes nowhere else. */}
          {request.reason ? <span className="rn-muted">{request.reason}</span> : null}
        </span>
        <span className="tc-row-side">
          <Pill variant={status.tone} dot>
            {status.word}
          </Pill>
          {canDecide ? (
            <button
              type="button"
              className="rn-btn-quiet"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? 'Cancel' : 'Decide'}
            </button>
          ) : null}
        </span>
      </div>

      {open && canDecide ? (
        <div style={{ padding: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
          <SettingsField
            label="Note (optional)"
            hint="Stored on the request and shown to the worker. A denial without one leaves them with no explanation."
          >
            <textarea
              style={{ marginBottom: 0, minHeight: 72 }}
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
            />
          </SettingsField>

          {/* FIVE OUTCOMES, FOUR OF THEM PERMANENT. Only `failed` gets a
              retry; a button under a refusal, a 404 or a 409 promises
              something the server has already answered. */}
          {failure ? <DecisionFailureNote failure={failure} /> : null}
          {failure?.kind === 'failed' ? (
            <ErrorPanel
              message={`${failure.message} Trying again repeats the ${
                attempted === 'approve' ? 'approval' : 'denial'
              }.`}
              onRetry={() => decide.mutate(attempted)}
            />
          ) : null}

          <div className="tc-drawer-actions">
            <button
              type="button"
              className="signin-devbtn"
              style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
              disabled={decide.isPending}
              onClick={() => decide.mutate('approve')}
            >
              {decisionButtonLabel(request.id, 'approve', running, 'Approve the leave')}
            </button>
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={decide.isPending}
              onClick={() => decide.mutate('deny')}
            >
              {decisionButtonLabel(request.id, 'deny', running, 'Deny')}
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/**
 * The four permanent outcomes, each said in its own words.
 *
 * `failed` is not here: it is the retryable one and is rendered by
 * `ErrorPanel`, which is the only branch entitled to a button.
 */
function DecisionFailureNote({ failure }: { failure: TimeOffDecisionFailure }) {
  if (failure.kind === 'refused') {
    return (
      <>
        <Refused heading="This one is not yours to decide" message={failure.message} />
        {/* Each structured refusal on its OWN LINE. `permission_not_held` and
            `scope_not_held` are different problems with different fixes — the
            first is answered by somebody granting you a key, the second by
            narrowing the grant you are attempting — and answering a scope
            refusal by widening a grant to organization scope is an escalation
            produced by a fallthrough branch. The joined `message` a ceiling 403
            carries cannot be read apart, so it is not printed. Empty for a
            plain 403, which is what this domain sends today. */}
        {failure.refusals.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 'var(--space-16)' }}>
            {failure.refusals.map((sentence) => (
              <li key={sentence} className="rn-muted">
                {sentence}
              </li>
            ))}
          </ul>
        ) : null}
      </>
    );
  }
  if (failure.kind === 'gone') {
    return <Refused heading="No longer yours to decide" message={failure.message} />;
  }
  if (failure.kind === 'settled') {
    return (
      <p className="rn-muted" role="status" aria-live="polite" style={{ margin: 0 }}>
        {failure.message}
      </p>
    );
  }
  if (failure.kind === 'rejected') {
    return (
      <p className="rn-muted" role="alert" style={{ margin: 0, color: 'var(--color-urgent-deep)' }}>
        {failure.message}
      </p>
    );
  }
  return null;
}
